/**
 * SESSION
 * Informations related to the session of an account
 */

import {
  AccountAdsOptions,
  AccountColors,
  AccountFeatures,
  AccountModules,
  AccountModulesSurvey,
  AccountOptions,
  AccountTemplateRule
} from "./account.types"
import { UserOptions } from "./user.types"
import languages from "@/translate/lang.json"
import { keys, pickBy, sortBy } from "lodash"
import { getIconUrl } from "@/utils/default-icons.utils"
import { t } from "@/translate/t"
import {
  faChartLine,
  faChartPie,
  faColumns,
  faComments,
  faExclamationTriangle,
  faList,
  faSmile,
  faTh,
  faUsers,
  faVoteYea,
  IconDefinition
} from "@fortawesome/free-solid-svg-icons"
import { Filter } from "./filter.types"
import { getImageUrl } from "@/utils/get-image-url"

export type CONNEXION_MODE = "GOOGLE" | "EMAIL" | "SMS"

//Survey
export type INTERFACE_TYPE = "ADMIN" | "ADMIN_PARENT" | "SUPERVISOR" //Interface for survey module

//Risks
export const SURVEY_ROLES = ["ADMIN", "OBSERVER"] as const //Admin = access to the whole app, Observer = access to only dashboard for his team
export type SURVEY_ROLE = (typeof SURVEY_ROLES)[number]

export type RISKS_ROLE = "SUPERADMIN" | "ADMIN" | "CONTRIBUTOR" | "USER" //Superadmin = Bruno (multiple), Admin = Gaëlle (unique but edit scales), Contributor = Djo (unique), User simple not Contributor & not admin
export type RISKS_INTERFACE_TYPE = "ORGANIZATION" | "ESTABLISHMENT" //Connected as
export type AccountDemoSurvey = "qvt" | "rps"

export const SESSION_DEFAULT_LANGUAGES = ["en", "fr"]
export type SessionDefaultLanguage = (typeof SESSION_DEFAULT_LANGUAGES)[number]

export interface SessionDashboardTab {
  id: string
  label: string
  isLarge?: boolean
  icon: IconDefinition
  hideDemo?: boolean
  module?: keyof AccountModulesSurvey
  role?: SURVEY_ROLE
  showForTrial?: boolean
}

export const SESSION_DASHBOARD_TABS: SessionDashboardTab[] = [
  { id: "/dashboard/overview", icon: faChartPie, label: "dashboard_overview" },
  { id: "/dashboard/participation", icon: faVoteYea, label: "survey_participation", hideDemo: true },
  { id: "/dashboard/populations", icon: faUsers, label: "attributes", module: "populations" },
  { id: "/dashboard/questions", icon: faList, label: "questions" },
  { id: "/dashboard/heatmap", icon: faTh, label: "dashboard_heatmap" },
  { id: "/dashboard/compare", icon: faColumns, label: "dashboard_compare", role: "ADMIN" },
  { id: "/dashboard/history", icon: faChartLine, label: "dashboard_history" },
  { id: "/dashboard/messages", icon: faComments, label: "dashboard_messages" },
  { id: "/dashboard/nps", icon: faSmile, label: "nps", showForTrial: true },
  { id: "/dashboard/rps", icon: faExclamationTriangle, label: "rps", showForTrial: true }
]

//Information of the idtree contact
export interface SessionSalesContact {
  name: string
  description: string
  phone: string
  imageUrl: string
  email: string
  calendly: string
  isActive: boolean
}

export interface SessionRole {
  feature: string | null
  id: string
  label: string
  itemIds: string[]
}

export class Session {
  accountAdsOptions: AccountAdsOptions = new AccountAdsOptions()
  accountColors: AccountColors = new AccountColors() //Colors for connected account
  accountId: string = "" //Id of the connected account
  accountInvitations: number = 0 //Count of invitations for the account
  accountIsChildren: boolean = false //The account depends of a super_account
  accountIsTrial: boolean = false //Limited features
  accountLastSubscriptionEnd: number | null = null //Expiration date of last subscription
  accountOptions: AccountOptions = new AccountOptions() //Options (image, preferences...)
  accountTemplateRule: AccountTemplateRule = "all" //Permissions for the templates
  accountAllowedTemplates: string[] = [] //Permissions for the templates
  accountDemoSurvey: AccountDemoSurvey = "qvt" //Demo survey selected for the account
  accountName: string = "" //Name of the connected account
  authLevel: number = 0 //Auth Level (0 => no auth, 1 => email, 2 => password)
  connectedAsSuperadmin: boolean = false //Is the user connected a superadmin
  connectedAsSupervisor: boolean = false //Is the user connected a supervisor
  connexionMode: CONNEXION_MODE = "EMAIL" //TYpe of connexion
  email: string = "" //Email of the connected user
  errorDetails: string | null = null //Display error details
  errorMessage: string | null = null //Display Error message on the bottom of the screen
  features: AccountFeatures = new AccountFeatures() //List of features for the account
  featureConnected: keyof AccountFeatures | null = null
  isPasswordRequired: boolean = false //Is password required for advanced roles (EDITOR and OBSERVER)
  isSuperAccount: boolean = false //Consulting account
  interfaceType: INTERFACE_TYPE = "ADMIN" //Type of interface (define the level of the app)
  language: SessionDefaultLanguage = "fr" //Current language displayed
  logoLastUpdate: number = new Date().getTime() //Refresh image cache when image is updated
  modules: AccountModules = new AccountModules() //List of optional modules
  onboardingModule: keyof AccountModules = "survey"
  participationMin: number = 5 //Ceil to display results
  phone: string = "" //User's phone number
  qvCode: string | null = null //Code for voting
  risksRole: RISKS_ROLE = "CONTRIBUTOR" //Role of the risk user
  risksInterfaceType: RISKS_INTERFACE_TYPE = "ESTABLISHMENT" //Type of interface for the risks
  salesContact: SessionSalesContact = getRandomSalesContact()
  selectedRole: SURVEY_ROLE = "ADMIN" //Filter roles when adding admins (by default empty)
  superaccountColor: string = "" //Color for the superaccount
  superaccountId: string = "" //Id of superaccount
  superaccountImage: boolean = true //Has the superaccount an image attached
  superaccountName: string = "" //Name for the superaccount
  supervisorId: string = "" //Id of the supervisor connected
  supervisorLevel: number = 0 //level of the supervisor connected
  establishmentId: string | null = null //Is user connected to an establishment
  userPasswordProvided: boolean = false //Has the user provided a password
  userId: string = "" //Id of the connected user
  userExcludedFilters: string[] = [] //List of filtres not visible for the user in the dashboard
  userFirstname: string = "" //Firstname of the connected user
  userInitialLanguage: string = "en" //Language if different of fr or en (for displaying texts)
  userLastname: string = "" //lastname of the connected user
  userOptions: UserOptions = new UserOptions() //Options of the user (preferences for app display)
  userRole: SURVEY_ROLE | null = null //Get roles of the survey app

  constructor(session: Partial<Session> = {}) {
    if (!session) session = new Session()
    session.accountColors = new AccountColors(session.accountColors)
    session.accountOptions = new AccountOptions(session.accountOptions)
    session.userOptions = new UserOptions(session.userOptions)
    Object.assign(this, session)
  }

  //Return settings options
  //If specific settings are available apply them
  get dashboardDisplaySettings(): AccountOptions {
    if (this.userOptions.dashboardApplyUserSettings && this.userRole === "ADMIN") {
      return new AccountOptions(this.userOptions)
    } else if (this.userOptions.dashboardApplyUserSettings && this.accountOptions.allowObserverSettings) {
      return new AccountOptions(this.userOptions)
    } else {
      return this.accountOptions
    }
  }

  //Get list of features by array
  get featuresList(): string[] {
    return keys(pickBy(this.features, Boolean))
  }

  //List of languages avaiable
  //First value : language for the account
  //Second value : english
  get languagesAvailables(): any[] {
    let list: any[] = languages.filter((x) => x.code !== this.language && x.code !== "en")
    list = list.map((x) => {
      return {
        id: x.code,
        name: x.name.split(";")[0].split(",")[0] + " (" + x.code.toUpperCase() + ")"
      }
    })
    list = sortBy(list, ["name"])

    if (this.language !== "en") {
      list.unshift({ id: "en", name: "EN (" + t("utils_default") + ")" })
    }

    list.unshift({
      id: this.language,
      name: this.language.toUpperCase() + " (" + t("utils_default") + ")"
    })

    return list
  }

  get userName(): string {
    if (this.userFirstname || this.userLastname) {
      return this.userFirstname + " " + this.userLastname
    } else {
      return this.email.split("@")[0]
    }
  }

  get accountImage(): string {
    return this.accountOptions.image
      ? getImageUrl("Accounts", this.accountId, this.logoLastUpdate)
      : getIconUrl(this.accountOptions.defaultIcon)
  }

  //Dashboard tabs for survey module
  getDashboardTabs(randomData: boolean, surveyCount: number) {
    return SESSION_DASHBOARD_TABS.filter((tab) => {
      //Hide for observers
      if (!this.isTabDisplayed(tab.id)) {
        return false
      }

      // Hide for demo
      if (tab.hideDemo && randomData) {
        return false
      }

      // Role restriction
      if (tab.role && this.userRole !== tab.role) {
        return false
      }

      // Trial access
      if (this.accountIsTrial && tab.showForTrial) {
        return true
      }

      // Module requirement
      if (tab.module && !this.modules.survey[tab.module]) {
        return false
      }

      //Multiple surveys for history
      if (tab.label === "dashboard_history" && surveyCount <= 1) {
        return false
      }

      return true
    })
  }

  getModuleObserver(module: keyof AccountModulesSurvey) {
    let isActive = this.modules.survey[module]

    if (this.userRole !== "ADMIN") {
      /*MAGIC BACKEND DHL */
      if (
        [
          "alfred.chantre@dhl.com",
          "anne.gessner@dhl.com",
          "assia.hakkou@dhl.com",
          "dominique.mouton@dhl.com",
          "jeanphilippe.grison@dhl.com",
          "matthieu.caldwell@dhl.com",
          "pascal.lamoureux@dhl.com",
          "raphael.rodriguez@dhl.com",
          "sylvie.noel@dhl.com",
          "veronique.gregoire@dhl.com",
          "viviane.herbert@dhl.com"
        ].includes(this.email)
      ) {
        return isActive
      }

      /* apply session parameter */
      if (module === "summarize") isActive = !this.accountOptions.hideSummarizeForObserver
      else if (module === "assistant") isActive = !this.accountOptions.hideAssistantForObserver
    }

    return isActive
  }

  //For observer apply rule to hide specific filters
  //SurveyId only for magic backend
  getVisibleFilters(filters: Filter[], surveyId?: string) {
    if (this.userRole === "OBSERVER") {
      /*
      MAGIC BACKEND
      */

      //DHL AVIATION
      //Show only Cost Center 1 for some specific users
      if (
        [
          "57b33f03-5d0b-44bc-8b62-0a39263e9cb5", //Martial
          "c3256db7-ce2b-46f6-a0da-71fb37a730f5", //Anthony
          "faf0ecef-1d59-4b2a-bafa-6bec9f5f8bec", //Karim
          "958982ab-1482-41d4-ba0e-6cc073ef7e44", //Aymen
          "067b1707-2b89-4b61-9f62-be292c494bd8" //Nicolas
        ].includes(this.userId)
      ) {
        filters = filters.filter((x) => x.name === "Cost Center 1 (code)")
      }
      //DHL EXPRESS
      //Hide cost center 1
      else if (surveyId === "d94f86b7-29dd-4a9e-b679-0971bb1d6b39") {
        filters = filters.filter((x) => !["Cost Center 1 (code)"].includes(x.name))
      }
      return filters.filter((x) => !this.accountOptions.hiddenFiltersForObserver.includes(x.name))
    } else {
      return filters
    }
  }

  isTabDisplayed(url: string) {
    return this.userRole === "ADMIN" || !this.accountOptions.dashboardTabsHiddenForObserver.includes(url)
  }
}

export const SESSION_LANGUAGES = [
  { id: "fr", value: "FR" },
  { id: "en", value: "EN" },
  { id: "de", value: "DE" },
  { id: "es", value: "ES" }
]

//Colors for picker (all colors)
export const SESSION_COLORS = [
  "#7d8ee0",
  "#95d5df",
  "#1b759a",
  "#094C72",
  "#61BD4F",
  "#49852E",
  "#FFAB4A",
  "#E99E40",
  "#EB5A46",
  "#933B27",
  "#F2D600",
  "#E6C60D",
  "#C377E0",
  "#6C547B",
  "#FF80CE",
  "#B44772",
  "#00C2E0",
  "#0082A0",
  "#51E898",
  "#4FD683",
  "#C4C9CC",
  "#B6BBBF",
  "#42548E",
  "#36405F"
]

//Pick a sales at random
function getRandomSalesContact() {
  let salesContacts: SessionSalesContact[] = [
    {
      name: "Thomas Röhm",
      description: "Expert QVT et co-fondateur de IDTree",
      phone: "+33 1 87 66 98 15",
      imageUrl: "https://idtree.fra1.digitaloceanspaces.com/Website/thomas.png",
      email: "thomas.rohm@id-tree.com",
      calendly: "https://calendly.com/idtree-thomas/reunion-15-minutes?utm_source=idtree_app",
      isActive: true
    },
    {
      name: "Samy Atmani",
      description: "Expert QVT",
      phone: "+33 1 87 66 98 15",
      imageUrl: "https://idtree.fra1.digitaloceanspaces.com/Website/samy.png",
      email: "samy.atmani@id-tree.com",
      calendly: "https://calendly.com/samy-atmani-id-tree/40min?utm_source=form_contact?utm_source=idtree_app",
      isActive: true
    },
    {
      name: "Cristian Gudima",
      description: "Expert QVT et co-fondateur de IDTree",
      phone: "+33 1 87 66 98 15",
      imageUrl: "https://idtree.fra1.digitaloceanspaces.com/Website/cris.png",
      email: "cristian.gudima@id-tree.com",
      calendly: "https://calendly.com/cristian-gudima/15min-1",
      isActive: true
    }
  ]

  salesContacts = salesContacts.filter((x) => x.isActive)
  if (salesContacts.length === 1) {
    return salesContacts[0]
  } else {
    const random = Math.random()
    if (random < 0.5) {
      return salesContacts[0]
    } else {
      return salesContacts[1]
    }
  }
}

export const SESSION_EDIT = "SESSION_EDIT"
export const SESSION_EDIT_ACCOUNT_COLORS = "SESSION_EDIT_ACCOUNT_COLORS"
export const SESSION_EDIT_ACCOUNT_OPTIONS = "SESSION_EDIT_ACCOUNT_OPTIONS"
export const SESSION_EDIT_USER_OPTIONS = "SESSION_EDIT_USER_OPTIONS"
export const SESSION_SHOW_ERROR = "SESSION_SHOW_ERROR"
export const SESSION_GET = "SESSION_GET"
export const SESSION_INIT = "SESSION_INIT"
